// iOS HIG building blocks shared across the portal pages
// - Bottom tab bar (49pt + safe area)
// - Portfolio value card (floating white card with eye toggle)
// - Big price hero (with sparkline, time range pills, Buy/Sell)
// - Stat row (3-up or 4-up)
// - List row with sparkline trailing
// - Section header
// - iOS form fields

import { ReactNode, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// ─── Bottom tab bar ─────────────────────────────────────────────────────────

interface TabItem {
  to: string;
  label: string;
  icon: ReactNode;
  testId?: string;
}

export function BottomTabBar({ items }: { items: TabItem[] }) {
  const loc = useLocation();
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex justify-center"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}
      data-testid="bottom-tabs"
    >
      <div
        className="flex items-center gap-1 rounded-full px-2 py-2"
        style={{
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.04)',
        }}
      >
        {items.map((it) => {
          const active = loc.pathname === it.to || (it.to !== '/portal/dashboard' && loc.pathname.startsWith(it.to));
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={[
                'flex items-center justify-center rounded-full transition-all',
                active
                  ? 'h-10 w-10 bg-tf-text-1 text-white'
                  : 'h-10 w-10 text-tf-text-3 hover:text-tf-text-1',
              ].join(' ')}
              aria-label={it.label}
              title={it.label}
            >
              <span className="flex h-5 w-5 items-center justify-center">
                {it.icon}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

// ─── Portfolio value card (floating white card, big number, eye toggle) ───

export function PortfolioCard({
  value,
  change,
  changeAbs,
  when,
  primaryAction, // { label, onClick } — single primary button (legacy)
  buyAction,     // { label, onClick } — for stock-app Buy pill
  sellAction,    // { label, onClick } — for stock-app Sell pill
}: {
  value: number;
  change: number; // percent
  changeAbs: number; // currency amount
  when?: string;
  primaryAction?: { label: string; onClick: () => void; icon?: ReactNode; testId?: string };
  buyAction?: { label: string; onClick: () => void };
  sellAction?: { label: string; onClick: () => void };
}) {
  const [visible, setVisible] = useState(true);
  const positive = change >= 0;
  return (
    <div
      className="relative -mt-10 mx-4 rounded-[24px] p-5 tf-anim-fadeInUp"
      style={{
        background: '#FFFFFF',
        boxShadow: '0 12px 40px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
      }}
    >
      <div className="text-center text-[13px] font-medium" style={{ color: '#8E8E93' }}>Portfolio value</div>
      <div className="mt-1 flex items-center justify-center gap-2">
        <div className="text-[40px] font-bold tabular-nums tracking-tight" style={{ color: '#1C1C1E', lineHeight: '1.1' }} data-testid="portfolio-value">
          {visible ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '••••••'}
        </div>
        <button
          onClick={() => setVisible(!visible)}
          className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-tf-surface-1 active:opacity-40"
          style={{ color: '#8E8E93' }}
          aria-label={visible ? 'Hide value' : 'Show value'}
        >
          {visible ? <EyeOpen /> : <EyeClosed />}
        </button>
      </div>
      <div className="mt-1 text-center text-[13px]">
        <span
          className="font-semibold"
          style={{ color: positive ? '#34C759' : '#FF3B30' }}
          data-testid="portfolio-change"
        >
          {positive ? '+' : ''}{change.toFixed(2)}% ({positive ? '+' : ''}${Math.abs(changeAbs).toFixed(2)})
        </span>
        <span className="ml-1" style={{ color: '#8E8E93' }}>· {when ?? 'Today'}</span>
      </div>
      {primaryAction && (
        <div className="mt-5">
          <button
            onClick={primaryAction.onClick}
            data-testid={primaryAction.testId}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.99]"
          >
            {primaryAction.icon}
            {primaryAction.label}
          </button>
        </div>
      )}
      {buyAction && sellAction && (
        <div className="mt-4 flex items-center gap-3" data-testid="portfolio-trade-buttons">
          <button
            onClick={buyAction.onClick}
            data-testid="portfolio-buy"
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white transition-transform active:scale-[0.97]"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M19 12l-7 7-7-7" /></svg>
            {buyAction.label}
          </button>
          <button
            onClick={sellAction.onClick}
            data-testid="portfolio-sell"
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white transition-transform active:scale-[0.97]"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></svg>
            {sellAction.label}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Time range pills (1D/7D/1M/3M/6M/1Y/ALL) ─────────────────────────────

const RANGES = ['1D', '7D', '1M', '3M', '6M', '1Y', 'ALL'] as const;
export type RangeKey = (typeof RANGES)[number];

export function TimeRange({ value, onChange }: { value: RangeKey; onChange: (r: RangeKey) => void }) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto py-2" data-testid="time-range">
      {RANGES.map((r) => (
        <button
          key={r}
          onClick={() => onChange(r)}
          data-testid={`range-${r}`}
          className={[
            'min-w-[40px] rounded-full px-3 py-1 text-[13px] font-semibold transition',
            r === value ? 'bg-tf-text-1 text-white' : 'text-tf-text-1 hover:bg-tf-surface-1',
          ].join(' ')}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

// ─── Big sparkline chart (line + filled area) ─────────────────────────────

export function Sparkline({
  points,
  color = '#1C1C1E',
  height = 120,
  fill = true,
  testId,
}: {
  points: number[];
  color?: string;
  height?: number;
  fill?: boolean;
  testId?: string;
}) {
  if (!points.length) {
    return <div className="flex h-24 items-center justify-center text-tf-text-3">No data</div>;
  }
  const w = 600;
  const h = height;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = w / (points.length - 1 || 1);
  const path = points.map((p, i) => {
    const x = i * stepX;
    const y = h - ((p - min) / range) * (h - 12) - 6;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const fillPath = `${path} L${w} ${h} L0 ${h} Z`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      data-testid={testId}
    >
      <defs>
        <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={fillPath} fill="url(#sparkfill)" />}
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Candlestick chart (mock — green up, red down bars) ───────────────────

export function Candles({ data, height = 220, testId }: { data: { o: number; h: number; l: number; c: number }[]; height?: number; testId?: string }) {
  if (!data.length) return <div className="h-32" />;
  const w = 600;
  const h = height;
  const all = data.flatMap(d => [d.h, d.l]);
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const candleW = Math.max(2, w / data.length - 2);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height }} data-testid={testId}>
      {data.map((d, i) => {
        const x = i * (w / data.length) + 1;
        const yO = h - ((d.o - min) / range) * (h - 12) - 6;
        const yC = h - ((d.c - min) / range) * (h - 12) - 6;
        const yH = h - ((d.h - min) / range) * (h - 12) - 6;
        const yL = h - ((d.l - min) / range) * (h - 12) - 6;
        const up = d.c >= d.o;
        const color = up ? '#34C759' : '#FF3B30';
        return (
          <g key={i}>
            <line x1={x + candleW / 2} y1={yH} x2={x + candleW / 2} y2={yL} stroke={color} strokeWidth="1" />
            <rect x={x} y={Math.min(yO, yC)} width={candleW} height={Math.max(1, Math.abs(yC - yO))} fill={color} />
          </g>
        );
      })}
    </svg>
  );
}

// ─── List row with mini sparkline trailing (gainers / holdings) ──────────

export function SparklineRow({
  icon, title, subtitle, value, change, points, onClick, testId,
}: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  value: ReactNode;
  change?: number;
  points?: number[];
  onClick?: () => void;
  testId?: string;
}) {
  const positive = (change ?? 0) >= 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex w-full items-center gap-3 border-b border-tf-border-strong/55 bg-white px-4 py-3 text-left active:bg-[#E5E5EA]/60"
    >
      {icon && <div className="shrink-0">{icon}</div>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold text-tf-text-1">{title}</div>
        {subtitle && <div className="truncate text-[12px] text-tf-text-3">{subtitle}</div>}
      </div>
      {points && points.length > 1 && (
        <div className="h-8 w-20 shrink-0">
          <Sparkline points={points} color={positive ? '#34C759' : '#FF3B30'} height={32} fill={false} />
        </div>
      )}
      <div className="w-20 shrink-0 text-right">
        <div className="tabular-nums text-[14px] font-semibold text-tf-text-1">{value}</div>
        {typeof change === 'number' && (
          <div className={['tabular-nums text-[12px]', positive ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
            {positive ? '+' : ''}{change.toFixed(2)}%
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Section header (uppercase, small) ────────────────────────────────────

export function SectionHeader({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 pb-2 pt-6">
      <h2 className="text-[13px] font-normal uppercase tracking-wide text-tf-text-3">{children}</h2>
      {right}
    </div>
  );
}

// ─── Pill button (white with thin border, used for inline actions) ───────

export function PillButton({ children, onClick, trailingIcon, className = '' }: { children: ReactNode; onClick?: () => void; trailingIcon?: ReactNode; className?: string }) {
  return (
    <button
      onClick={onClick}
      className={['inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-semibold transition active:scale-[0.99]', className].join(' ')}
    >
      {children}
      {trailingIcon}
    </button>
  );
}

// ─── iOS-styled Card (white rounded) ──────────────────────────────────────

export function Card({ children, className = '', title }: { children: ReactNode; className?: string; title?: ReactNode }) {
  return (
    <div className={['rounded-[16px] bg-white p-5', className].join(' ')}>
      {title && <h3 className="mb-3 text-[17px] font-bold text-tf-text-1">{title}</h3>}
      {children}
    </div>
  );
}

// ─── iOS input field ──────────────────────────────────────────────────────

export function FormField({ label, value, onChange, placeholder, name, type = 'text', maxLength, inputMode, suffix, leading, testId }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; name: string; type?: string; maxLength?: number; inputMode?: 'text' | 'numeric' | 'decimal' | 'email'; suffix?: ReactNode; leading?: ReactNode; testId?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-[13px] font-medium text-tf-text-2">{label}</label>
      <div className="relative">
        {leading && <div className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-tf-text-3">{leading}</div>}
        <input
          id={name}
          name={name}
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          data-testid={testId}
          className={['w-full rounded-[10px] border border-tf-border-strong bg-white px-4 py-3 text-[17px] text-tf-text-1 placeholder:text-tf-text-muted outline-none transition focus:border-[#007AFF]', leading ? 'pl-10' : '', suffix ? 'pr-12' : ''].join(' ')}
        />
        {suffix && <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[15px] font-medium text-tf-text-3">{suffix}</div>}
      </div>
    </div>
  );
}

// ─── iOS primary (black) button ───────────────────────────────────────────

export function PrimaryButton({ children, onClick, disabled, type = 'button', loading, testId, fullWidth = true, className = '' }: {
  children: ReactNode; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit'; loading?: boolean; testId?: string; fullWidth?: boolean; className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      className={[
        'flex h-[50px] items-center justify-center gap-2 rounded-[12px] bg-tf-text-1 text-[17px] font-semibold text-white transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-30',
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
    >
      {loading ? 'Working…' : children}
    </button>
  );
}

// ─── iOS secondary (filled gray) button ───────────────────────────────────

export function SecondaryButton({ children, onClick, type = 'button', testId, className = '' }: { children: ReactNode; onClick?: () => void; type?: 'button' | 'submit'; testId?: string; className?: string }) {
  return (
    <button
      type={type}
      onClick={onClick}
      data-testid={testId}
      className={['flex h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-tf-surface-1 text-[17px] font-semibold text-tf-text-1 transition active:scale-[0.99]', className].join(' ')}
    >
      {children}
    </button>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function EyeOpen() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeClosed() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

// ─── Empty / loading / error states ──────────────────────────────────────

export function Empty({ title, body }: { title: string; body?: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[16px] bg-white px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-tf-blue/10 text-tf-blue">
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3" /></svg>
      </div>
      <h3 className="mt-4 text-[17px] font-semibold text-tf-text-1">{title}</h3>
      {body && <p className="mt-1 text-[13px] text-tf-text-3">{body}</p>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-tf-text-3">
      <svg className="mr-2 h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="40" strokeDashoffset="20" />
      </svg>
      <span className="text-[15px]">{label}</span>
    </div>
  );
}

export function ErrorBox({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="rounded-[10px] border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-3 py-2.5 text-[13px] text-tf-loss">
      {children}
    </div>
  );
}

// iOS text area
export function TextArea({ label, value, onChange, placeholder, name, rows = 4 }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; name: string; rows?: number }) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-[13px] font-medium text-tf-text-2">{label}</label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full rounded-[10px] border border-tf-border-strong bg-white px-4 py-3 text-[15px] text-tf-text-1 placeholder:text-tf-text-muted outline-none transition focus:border-[#007AFF]"
      />
    </div>
  );
}

// iOS toggle row (a setting with on/off switch)
export function ToggleRow({ checked, onChange, label, id }: { checked: boolean; onChange: (v: boolean) => void; label: string; id?: string }) {
  return (
    <label htmlFor={id} className="flex w-full items-center justify-between gap-3 px-4 py-3 cursor-pointer active:bg-[#E5E5EA]/60 border-b border-tf-border-strong/55 last:border-b-0">
      <span className="text-[15px] text-tf-text-1">{label}</span>
      <span className={['flex h-7 w-12 items-center rounded-full transition', checked ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'].join(' ')}>
        <span className={['h-6 w-6 rounded-full bg-white shadow transition', checked ? 'ml-[22px]' : 'ml-0.5'].join(' ')} />
      </span>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
    </label>
  );
}
